import { TranslateLoader, TranslateService, TranslateModuleConfig } from '@ngx-translate/core';
import { catchError, delay, filter, firstValueFrom, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { Store } from '@ngrx/store';
import { Inject, Injectable, InjectionToken, Injector } from '@angular/core';
import { ArmyBuilderConfig } from '../config';
import { SelectTranslations, TRANSLATION_ACTIONS } from '../state';
import { AppInjector, deepMergeObjects, GAME_ID, initTranslateService, RestDataService } from 'army-builder-shared';

export const CustomLoaderCache: { [key: string]: { loaded: boolean; translateService: TranslateService; proof: Observable<any> } } = {};
export const defaultTranslateConfig: TranslateModuleConfig = {
    fallbackLang: 'en',
    isolate: true
};

export const getTranslateServiceForGame = (gameId: string) => {
    if (CustomLoaderCache[gameId]) {
        const currentLoader = CustomLoaderCache[gameId].translateService.currentLoader as CustomLoader;
        if (currentLoader.gameId !== gameId) {
            console.error(`Wrong TranslateService.  Expected ${gameId} but got ${currentLoader.gameId}!`);
        }
        return CustomLoaderCache[gameId].translateService;
    }
    return loadTranslateServiceForGame(gameId);
};

const loadTranslateServiceForGame = (gameId: string) => {
    if (CustomLoaderCache[gameId]) {
        return CustomLoaderCache[gameId].translateService;
    }

    const gameCustomLoader = new CustomLoader(gameId);
    gameCustomLoader.setInjector(AppInjector);

    // Create an isolated injector for this game's TranslateService
    const childInjector = Injector.create({
        providers: [TranslateService, { provide: TranslateLoader, useValue: gameCustomLoader }],
        parent: AppInjector
    });

    const translateService = childInjector.get(TranslateService);

    CustomLoaderCache[gameId] = {
        proof: null,
        loaded: false,
        translateService: translateService
    };

    initTranslateService(gameId);

    return CustomLoaderCache[gameId].translateService;
};

export const initGameTranslateService = (gameId: string) => {
    const translateService = getTranslateServiceForGame(gameId);
    return translateService.reloadLang('en');
};

export const waitForGameTranslateService = <T>(gameId: string) => {
    return function (source: Observable<T>): Observable<T> {
        return source.pipe(
            switchMap((val) => {
                const ts = getTranslateServiceForGame(gameId);

                if (!CustomLoaderCache[gameId].proof) {
                    let proof = ts.get('GLOBAL.OK').pipe(
                        delay(1), // Hacky, but it ensures the translations are loaded in something like MAP_WEAPON_NAME (bolt-action-3e data.ts)
                        switchMap(() => ts.reloadLang('en')),
                        shareReplay(1)
                    );

                    CustomLoaderCache[gameId].proof = proof;
                }

                return CustomLoaderCache[gameId].proof.pipe(switchMap((_) => source));
            })
        );
    };
};

export const LOAD_LANGUAGE_DATA_FN = new InjectionToken<(gameId: string, lang: string, fallbackToCache: boolean) => Promise<Object>>(
    'LOAD_LANGUAGE_DATA_FN'
);

@Injectable({ providedIn: 'root' })
export class CustomLoader extends TranslateLoader {
    protected translationsByGameId: any = {};

    private initialized = false;

    protected config: ArmyBuilderConfig;
    protected store: Store;

    protected injector: Injector;
    protected loadLanguageDataFn: (gameId: string, lang: string, fallbackToCache: boolean) => Promise<Object>;
    protected restDataService: RestDataService;

    constructor(@Inject(GAME_ID) public gameId: string) {
        super();
    }

    setInjector(injector: Injector) {
        this.injector = injector;
    }

    async loadData(gameId: string, lang: string, fallbackToCache = true) {
        this.init();

        const bundled = this.config.bundledTranslations?.[gameId]?.[lang];

        this.store.dispatch(TRANSLATION_ACTIONS.TRANSLATION_LOADING({ gameId, lang }));

        if (bundled) {
            // Dispatch bundled translations immediately so the UI is unblocked
            this.store.dispatch(TRANSLATION_ACTIONS.TRANSLATION_LOADED({ gameId, lang, data: bundled }));
        }

        // Attempt to load from API/DB — the result will be merged on top of bundled data
        try {
            let data = await (this.loadLanguageDataFn || this.defaultLoadLanguageFn)(gameId, lang, fallbackToCache);

            if (bundled) {
                data = deepMergeObjects(bundled, data);
            }

            console.log(`CustomLoader: Translations loaded (${gameId}:${lang})`);
            this.store.dispatch(TRANSLATION_ACTIONS.TRANSLATION_LOADED({ gameId, lang, data }));
        } catch (err) {
            console.error(`Error loading translations for ${gameId}:`, gameId, err);
            if (!bundled) {
                // Only dispatch error if we have no bundled fallback
                setTimeout(() => {
                    this.store.dispatch(TRANSLATION_ACTIONS.TRANSLATION_ERROR({ gameId, lang }));
                }, 1000);
            }
        }
    }
    private defaultLoadLanguageFn = async (gameId: string, lang: string, fallbackToCache: boolean) => {
        let apiBaseUrl = this.config.apiBaseUrl;
        const url = `${apiBaseUrl}/translation/${gameId}/${lang}`;

        let translations = await firstValueFrom(
            this.restDataService.get(url, {
                cacheKey: `translation_${gameId}_${lang}`,
                preferCache: false,
                fallbackToCache
            })
        );

        return translations.value;
    };

    protected init() {
        if (!this.initialized) {
            // Set the injector here when we actually need it, ensuring AppInjector is available
            if (!this.injector) {
                this.injector = AppInjector;
            }

            this.loadLanguageDataFn = this.injector.get(LOAD_LANGUAGE_DATA_FN, null);
            this.config = this.injector.get(ArmyBuilderConfig);
            this.restDataService = this.injector.get(RestDataService);
            this.store = this.injector.get(Store);
            this.initialized = true;
        }
    }

    public getTranslation(lang: string): Observable<any> {
        this.init();
        const gameId = this.gameId;
        if (!this.translationsByGameId[gameId]) {
            this.translationsByGameId[gameId] = {};
        }

        if (!this.translationsByGameId[gameId][lang]) {
            this.translationsByGameId[gameId][lang] = this.store.select(SelectTranslations).pipe(
                map((translations) => {
                    const languageTranslations = translations?.[lang];
                    const gameTranslations = languageTranslations?.[gameId]?.data;
                    const globalTranslations = languageTranslations?.['global']?.data;
                    return { gameTranslations, globalTranslations };
                }),
                filter(({ gameTranslations, globalTranslations }) => !!globalTranslations),
                map(({ gameTranslations, globalTranslations }) => {
                    if (!gameTranslations) {
                        return {};
                    }

                    const res = {
                        ...gameTranslations,
                        GLOBAL: deepMergeObjects(globalTranslations, gameTranslations?.GLOBAL || {})
                    };

                    return res;
                }),
                filter((res) => Object.keys(res)?.length > 0),
                catchError((err) => {
                    setTimeout(() => {
                        this.store.dispatch(TRANSLATION_ACTIONS.TRANSLATION_ERROR({ gameId, lang }));
                    }, 1000);
                    return of({});
                })
            );
        }

        return this.translationsByGameId[gameId][lang];
    }
}
